/* Transport kept separate so rate polling and error handling can be tested. */
(function (root) {
  'use strict';
  const wait = (ms, signal) => new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(new DOMException('Aborted', 'AbortError'));
    const stop = () => { clearTimeout(timer); reject(new DOMException('Aborted', 'AbortError')); };
    const timer = setTimeout(() => { signal?.removeEventListener('abort', stop); resolve(); }, ms);
    signal?.addEventListener('abort', stop, { once: true });
  });
  function address(zip, province) {
    if (!/^\d{5}-?\d{3}$/.test(zip.trim())) throw new Error('Informe um CEP válido com 8 números.');
    if (province !== undefined && !'AC AL AP AM BA CE DF ES GO MA MT MS MG PA PB PR PE PI RJ RN RS RO RR SC SP SE TO'.split(' ').includes(province)) throw new Error('Selecione o estado de entrega.');
    const params = new URLSearchParams({ 'shipping_address[zip]': zip.trim().replace('-', ''), 'shipping_address[country]': 'Brazil' });
    if (province) params.set('shipping_address[province]', province);
    return params.toString();
  }
  function rateMoney(price, currency, locale = 'pt-BR') {
    if (price === null || price === '' || !Number.isFinite(Number(price)) || Number(price) < 0) return 'Valor confirmado no checkout';
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(Number(price));
  }
  function discountCodes(values) {
    const seen = new Set();
    return (Array.isArray(values) ? values : String(values || '').split(',')).map(value => String(value).trim()).filter(value => {
      const key = value.toLocaleLowerCase();
      if (!value || seen.has(key)) return false;
      seen.add(key); return true;
    });
  }
  function discountResult(cart, code, renderedCodes = []) {
    const key = String(code).trim().toLocaleLowerCase();
    const matching = value => String(value || '').trim().toLocaleLowerCase() === key;
    const known = cart?.discount_codes?.find(item => matching(item.code));
    if (known?.applicable === true) return 'applied';
    if (known?.applicable === false) return 'not_applicable';
    if (renderedCodes.some(matching)) return 'applied';
    const allocations = [...(cart?.cart_level_discount_applications || []), ...(cart?.items || []).flatMap(item => (item.line_level_discount_allocations || []).map(allocation => ({ ...allocation.discount_application, total_allocated_amount: allocation.amount })))];
    return allocations.some(item => item.type === 'discount_code' && matching(item.title) && Number(item.total_allocated_amount) > 0) ? 'applied' : 'unconfirmed';
  }
  function cartSummary({ total, currency = 'BRL', requiresShipping = true, pixPercent = 0, pixApplied = 0 }, rate = null) {
    const subtotal = Number(total), percent = Number(pixPercent), applied = Number(pixApplied);
    if (!Number.isSafeInteger(subtotal) || subtotal < 0 || !Number.isSafeInteger(applied) || applied < 0 || !Number.isSafeInteger(subtotal + applied)) throw new Error('Confira o total no checkout.');
    const discount = Number.isFinite(percent) ? Math.min(100, Math.max(0, percent)) : 0;
    const shippingAmount = rate?.price;
    const validRate = rate !== null && shippingAmount !== null && shippingAmount !== '' && Number.isFinite(Number(shippingAmount)) && Number(shippingAmount) >= 0 && (!rate.currency || rate.currency === currency);
    const shippingCents = validRate ? Math.round(Number(shippingAmount) * 100) : null;
    const quoted = requiresShipping && Number.isSafeInteger(shippingCents);
    const shipping = quoted ? shippingCents : 0;
    const base = subtotal + applied;
    const card = base + shipping;
    if (!Number.isSafeInteger(card)) throw new Error('Confira o total no checkout.');
    const discounted = amount => Math.round(amount * (100 - discount) / 100);
    // Preserve the actual merchandise allocation, including Shopify rounding and
    // coupon combinations. Freight is added in full for either payment method.
    const pix = (applied > 0 ? subtotal : discounted(base)) + shipping;
    return { subtotal, pix, card, installment: Math.round(card / 3), shipping: requiresShipping ? quoted ? shipping : null : 0, quoted, requiresShipping };
  }
  function serviceRates(data, currency, total) {
    if (!Array.isArray(data?.rates)) throw new Error('Não foi possível confirmar o frete. Consulte no checkout.');
    const cents = value => (typeof value === 'number' || typeof value === 'string' && /^\d+$/.test(value)) && Number.isSafeInteger(Number(value)) && Number(value) >= 0;
    return data.rates.map(rate => {
      if (!cents(rate.total_price) || rate.currency && rate.currency !== currency || total !== undefined && !cents(total)) throw new Error('A cotação retornou um valor inválido. Consulte no checkout.');
      const price = Number(rate.total_price);
      const full = cents(rate.full_price) ? Number(rate.full_price) : price;
      const bps = Number(rate.subsidy_bps);
      const subsidyBps = full > price && Number.isInteger(bps) && bps > 0 && bps <= 10000 ? bps : 0;
      return { name: rate.service_name || 'Entrega', price: price / 100, fullPrice: subsidyBps ? full / 100 : null, subsidyBps, currency: currency || 'BRL', cartTotal: total === undefined ? undefined : Number(total) / 100, delivery_days: cents(rate.delivery_days) ? [Number(rate.delivery_days)] : [], businessDays: true };
    }).sort((a, b) => a.price - b.price);
  }
  function createClient({ fetcher = fetch, sleep = wait, root = '/', add, change, cart, shippingEndpoint }) {
    root = root.replace(/\/?$/, '/');
    async function request(url, options = {}) {
      const controller = new AbortController();
      const cancel = () => controller.abort();
      options.signal?.addEventListener('abort', cancel, { once: true });
      if (options.signal?.aborted) controller.abort();
      const timeout = setTimeout(cancel, 15000);
      let response;
      try {
        response = await fetcher(url, { credentials: 'same-origin', ...options, signal: controller.signal, headers: { Accept: 'application/json', ...options.headers } });
      } catch (error) {
        if (options.signal?.aborted) throw new DOMException('Aborted', 'AbortError');
        throw new Error('A conexão demorou ou foi interrompida. Confira sua sacola e tente novamente.');
      } finally { clearTimeout(timeout); options.signal?.removeEventListener('abort', cancel); }
      let data;
      try { data = await response.json(); } catch { throw new Error('Não foi possível ler a resposta da loja. Confira sua sacola antes de tentar novamente.'); }
      if (!response.ok) {
        const message = typeof data?.description === 'string' ? data.description : typeof data?.message === 'string' ? data.message : 'Não foi possível concluir. Confira o endereço, a disponibilidade e tente novamente.';
        throw new Error(message);
      }
      return data;
    }
    return {
      add(form) { form.set('sections', 'cart-drawer'); form.set('sections_url', cart); return request(add + '.js', { method: 'POST', body: form }); },
      addItems(items) { return request(add + '.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items, sections: ['cart-drawer'], sections_url: cart }) }); },
      change(id, quantity) { return request(change + '.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id, quantity, sections: ['cart-drawer'], sections_url: cart }) }); },
      applyDiscount(codes) { return request(root + 'cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ discount: discountCodes(codes).join(','), sections: ['cart-drawer'], sections_url: cart }) }); },
      // /cart negotiates a bare cart payload for Accept: application/json, ignoring sections.
      section() { return request(cart + '?sections=cart-drawer', { headers: { Accept: 'text/html' } }); },
      async productShipping(zip, item, currency, signal) {
        address(zip);
        if (!shippingEndpoint) throw new Error('Consulte o frete na sacola ou no checkout.');
        if (!/^\d+$/.test(String(item.variant_id)) || Number(item.variant_id) <= 0 || !Number.isSafeInteger(item.quantity) || item.quantity < 1 || !Number.isSafeInteger(item.grams) || item.grams < 0 || !Number.isSafeInteger(item.price) || item.price < 0) throw new Error('Confira a apresentação e a quantidade para calcular o frete.');
        if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
        const { variant_id, quantity, grams, price } = item;
        const data = await request(shippingEndpoint, { method: 'POST', signal, credentials: 'omit', referrerPolicy: 'no-referrer', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ mode: 'pdp', cep: zip.trim().replace('-', ''), items: [{ variant_id, quantity, grams, price }] }) });
        if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
        return serviceRates(data, currency);
      },
      async shipping(zip, province, signal) {
        address(zip, province);
        if (shippingEndpoint) {
          const basket = await request(cart + '.js', { signal });
          if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
          const items = basket.items.filter(item => item.requires_shipping !== false).map(({ variant_id, quantity, grams, price }) => ({ variant_id, quantity, grams, price }));
          if (!items.length) return [];
          const data = await request(shippingEndpoint, { method: 'POST', signal, credentials: 'omit', referrerPolicy: 'no-referrer', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ mode: 'cart', cep: zip.trim().replace('-', ''), items }) });
          if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
          return serviceRates(data, basket.currency, basket.total_price);
        }
        if (province === undefined) {
          const result = await request('https://viacep.com.br/ws/' + zip.trim().replace('-', '') + '/json/', { signal, credentials: 'omit', referrerPolicy: 'no-referrer' });
          if (result?.erro || !result?.uf) throw new Error('CEP não encontrado. Confira os números e tente novamente.');
          province = result.uf;
        }
        const query = address(zip, province);
        await request(root + 'cart/prepare_shipping_rates.json?' + query, { method: 'POST', signal });
        for (let attempt = 0; attempt < 9; attempt++) {
          await sleep(Math.min(600 + attempt * 400, 2500), signal);
          const data = await request(root + 'cart/async_shipping_rates.json?' + query, { signal });
          if (data !== null) {
            if (!Array.isArray(data.shipping_rates)) throw new Error('A loja não retornou opções de entrega válidas. Consulte no checkout.');
            return data.shipping_rates;
          }
        }
        throw new Error('A transportadora está demorando. Tente novamente ou consulte no checkout.');
      }
    };
  }
  const api = { createClient, address, rateMoney, discountCodes, discountResult, cartSummary };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.BeerClubCommerce = api;
})(typeof window !== 'undefined' ? window : globalThis);
